import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Menu, X, LayoutDashboard, Coins } from 'lucide-react';
import { Button } from './ui/button';
import LanguageSwitcher from './LanguageSwitcher';
import UserDropdown from './UserDropdown';
import { NotificationBell } from './NotificationBell';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NavCredits = () => {
  const [balance, setBalance] = useState(null);
  useEffect(() => {
    axios.get(`${API}/beta/wallet`).then(r => setBalance(r.data.credit_balance)).catch(() => {});
  }, []);
  if (balance === null) return null;
  return (
    <Link
      to="/beta"
      className="hidden lg:inline-flex items-center gap-1.5 px-3 h-9 rounded-full border border-amber-300 bg-amber-50 text-amber-800 text-sm font-semibold hover:bg-amber-100 transition-colors whitespace-nowrap"
      title="NCH utility credits - click to manage"
      data-testid="navbar-credits"
    >
      <Coins className="w-3.5 h-3.5" /> {balance} NCH
    </Link>
  );
};

const Navbar = () => {
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { t } = useTranslation();
  const { user, token } = useAuth();
  const homePath = user?.role === 'admin' ? '/admin' : '/dashboard';

  return (
    <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-200" data-testid="main-navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        {/* Brand */}
        <Link to="/" className="flex items-center gap-2.5 group shrink-0 mr-4" data-testid="navbar-brand">
          <img src="/logo.png" alt="NotaryChain seal" className="w-9 h-9 rounded-full transition-transform group-hover:scale-105" data-testid="navbar-logo" />
          <span className="font-serif text-xl font-bold text-navy-900">NotaryChain</span>
        </Link>

        {/* Desktop nav */}
        <div className="hidden lg:flex items-center gap-3 xl:gap-5 whitespace-nowrap pl-6">
          <NavLink to="/notaries">Notary directory</NavLink>
          <NavLink to="/library">Library</NavLink>
          <Link to="/demo" className="text-sm font-medium text-slate-700 hover:text-coral-600 transition-colors inline-flex items-center gap-1" data-testid="navbar-demo">
            Try a demo <span className="inline-block px-1.5 py-0.5 text-[9px] font-bold tracking-wider bg-coral-500 text-white rounded">NEW</span>
          </Link>
          <NavLink to="/verify">Verify</NavLink>
          <NavLink to="/trust-badge">Trust badge</NavLink>
          <NavLink to="/pricing">{t('nav.pricing')}</NavLink>
          <LanguageSwitcher />
          {user ? (
            <>
              <NavCredits />
              <NotificationBell token={token} />
              <Button
                onClick={() => navigate(homePath)}
                className="bg-navy-800 hover:bg-navy-900 text-white h-10 px-5 rounded-md shadow-sm font-medium"
                data-testid="navbar-dashboard"
              >
                <LayoutDashboard className="w-4 h-4 mr-1.5" /> Dashboard
              </Button>
              <UserDropdown />
            </>
          ) : (
            <>
              <Link to="/login" className="text-sm font-medium text-navy-900 hover:text-coral-600 transition-colors px-2" data-testid="navbar-login">
                {t('nav.login')}
              </Link>
              <Button
                onClick={() => navigate('/signup')}
                className="bg-coral-500 hover:bg-coral-600 text-white h-10 px-5 rounded-md shadow-sm font-medium"
                data-testid="navbar-signup"
              >
                {t('nav.signup')}
              </Button>
            </>
          )}
        </div>

        {/* Mobile: user menu + hamburger */}
        <div className="lg:hidden flex items-center gap-1">
          {user && <NotificationBell token={token} />}
          {user && <UserDropdown />}
          <button
            className="text-navy-900 hover:text-coral-600 p-2"
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="lg:hidden border-t border-slate-200 bg-white px-4 py-4 space-y-1" data-testid="mobile-menu">
          {[
            ['/notaries', 'Notary directory'],
            ['/demo', 'Try a demo · NEW'],
            ['/verify', 'Verify'],
            ['/trust-badge', 'Trust badge'],
            ['/pricing', t('nav.pricing')],
            ...(user ? [[homePath, 'Dashboard']] : [['/login', t('nav.login')]]),
          ].map(([href, label]) => (
            <Link
              key={href}
              to={href}
              onClick={() => setMobileOpen(false)}
              className="block text-navy-900 hover:bg-cream-200 rounded-md text-sm font-medium py-2.5 px-3"
            >
              {label}
            </Link>
          ))}
          <div className="py-2 px-3">
            <LanguageSwitcher />
          </div>
          {!user && (
            <Button
              onClick={() => { navigate('/signup'); setMobileOpen(false); }}
              className="w-full bg-coral-500 hover:bg-coral-600 text-white py-2.5 rounded-md font-medium"
            >
              {t('nav.signup')}
            </Button>
          )}
        </div>
      )}
    </nav>
  );
};

function NavLink({ to, children }) {
  return (
    <Link to={to} className="text-sm font-medium text-slate-700 hover:text-coral-600 transition-colors">
      {children}
    </Link>
  );
}

// Inline brand seal - kept for embeds that still reference it
// eslint-disable-next-line no-unused-vars
function Seal({ className }) {
  return (
    <svg viewBox="0 0 64 64" className={className} aria-hidden="true">
      <circle cx="32" cy="32" r="29" fill="#0A192F" />
      <circle cx="32" cy="32" r="24" fill="none" stroke="#D4AF37" strokeWidth="1.2" />
      <circle cx="32" cy="32" r="13" fill="none" stroke="#D4AF37" strokeWidth="0.8" />
      <g stroke="#D4AF37" strokeWidth="1.4" strokeLinecap="round">
        {[0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330].map(d => (
          <line key={d} x1="32" y1="5" x2="32" y2="9" transform={`rotate(${d} 32 32)`} />
        ))}
      </g>
      <path d="M32 24 L36 42 L32 45 L28 42 Z" fill="#D4AF37" />
    </svg>
  );
}

export default Navbar;
